// TermsAndConditionsPage.tsx
import React from 'react';

const TermsAndConditionsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-gray-800 to-gray-900 px-6 py-8 sm:px-10 sm:py-10">
            <h1 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Terms & Conditions
            </h1>
            <p className="text-gray-300 mt-2 text-lg">
              Vishwaraja Enterprises
            </p>
            <p className="text-gray-400 text-sm mt-4">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
          
          <div className="px-6 py-8 sm:px-10 sm:py-8">
            <div className="prose prose-indigo max-w-none">
              <p className="text-gray-600 text-lg leading-relaxed">
                Welcome to Vishwaraja Enterprises. By accessing or using our website and services, 
                you agree to be bound by these Terms & Conditions. Please read them carefully before 
                using our services.
              </p>
            </div>
          </div>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6">
          {/* Section 1 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">1. Acceptance of Terms</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                By using our website, products, or services, you acknowledge that you have read, 
                understood, and agree to be bound by these Terms & Conditions. If you do not agree 
                with any part of these terms, you must not use our services.
              </p>
            </div>
          </div>

          {/* Section 2 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">2. Products and Services</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed mb-3">
                Vishwaraja Enterprises offers various products and services. We reserve the right to:
              </p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-gray-700 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Modify or discontinue any product or service without notice
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-gray-700 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Update prices and specifications at any time
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-gray-700 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Limit quantities of any products or services
                </li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">3. User Responsibilities</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed mb-3">As a user of our services, you agree to:</p>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-gray-700 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Provide accurate and complete information
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-gray-700 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Maintain the confidentiality of your account credentials
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-gray-700 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Not use our services for any unlawful purpose
                </li>
                <li className="flex items-start">
                  <svg className="h-5 w-5 text-gray-700 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                  Not interfere with or disrupt our services
                </li>
              </ul>
            </div>
          </div>

          {/* Section 4 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">4. Payments and Billing</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                All payments made to Vishwaraja Enterprises are subject to our billing terms. 
                We accept various payment methods and reserve the right to change our pricing 
                with prior notice. Refunds are handled on a case-by-case basis according to 
                our refund policy.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  Secure Payments
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  Invoice Available
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                  GST Compliant
                </span>
              </div>
            </div>
          </div>

          {/* Section 5 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">5. Intellectual Property</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                All content, logos, trademarks, and materials on our website are the property 
                of Vishwaraja Enterprises and are protected by applicable intellectual property 
                laws. You may not reproduce, distribute, or create derivative works without 
                our explicit written consent.
              </p>
            </div>
          </div>

          {/* Section 6 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">6. Limitation of Liability</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                To the maximum extent permitted by law, Vishwaraja Enterprises shall not be 
                liable for any indirect, incidental, special, consequential, or punitive damages, 
                or any loss of profits or revenues, whether incurred directly or indirectly, 
                or any loss of data, use, goodwill, or other intangible losses resulting from 
                your use of our services.
              </p>
            </div>
          </div>

          {/* Section 7 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">7. Termination</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                We may terminate or suspend your access to our services immediately, without 
                prior notice or liability, for any reason whatsoever, including without limitation 
                if you breach these Terms & Conditions. Upon termination, your right to use the 
                services will cease immediately.
              </p>
            </div>
          </div>

          {/* Section 8 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">8. Governing Law</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                These Terms shall be governed and construed in accordance with the laws of India, 
                without regard to its conflict of law provisions. Any disputes arising under these 
                terms shall be subject to the exclusive jurisdiction of the courts in [Your City], India.
              </p>
            </div>
          </div>

          {/* Section 9 */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow duration-300">
            <div className="border-l-4 border-gray-800 bg-gray-50 px-6 py-4">
              <h2 className="text-xl font-semibold text-gray-800">9. Changes to Terms</h2>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-600 leading-relaxed">
                Vishwaraja Enterprises reserves the right to modify these Terms & Conditions at any time. 
                We will notify users of any material changes by posting the new terms on this page. 
                Your continued use of the services after such modifications constitutes acceptance of the new terms.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="mt-8 bg-gray-50 rounded-xl p-6 border border-gray-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                <svg className="h-5 w-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-800">Questions About Terms?</h3>
              <p className="text-gray-600 mt-1">
                If you have any questions regarding these Terms & Conditions, please contact our legal team.
              </p>
              <div className="mt-3">
                <a href="mailto:legal@vishwaraja.com" className="inline-flex items-center text-gray-700 hover:text-gray-900 font-medium">
                  legal@vishwaraja.com
                  <svg className="h-4 w-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500 border-t border-gray-200 pt-6">
          <p>© {new Date().getFullYear()} Vishwaraja Enterprises. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;